import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CaretLeft,
  CaretRight,
  Check,
  ClockCounterClockwise,
  Coins,
  Info,
  Ticket,
  UserPlus,
  UsersThree,
  WarningCircle,
  X,
} from '@phosphor-icons/react';

import { listMyOrganizations } from '@/api/orgs';
import { listTeams } from '@/api/teams';
import { listMembers } from '@/api/members';
import {
  approveTryout,
  bookSeat,
  createTryout,
  declineTryout,
  getCollectionStatus,
  getNoticeConfig,
  getSeatSummary,
  getSubscriptionMetrics,
  listSubscriptions,
  cancelSubscription,
  startCardSetup,
  type CardSetupInit,
  listPendingApprovals,
  listSeats,
  listTeamNoticeOverview,
  previewNoticeRun,
  releaseSeat,
  sendNoticeRun,
  type Period,
  type Preview,
  type PreviewRow,
  type FeeReason,
  type TeamNoticeStat,
  type Subscription,
} from '@/api/notices';
import type { Team } from '@/api/teams';
import { AccessDenied } from '@/components/AccessDenied';
import { CollectionDashboard } from '@/components/notices/CollectionDashboard';
import { CardSetupForm } from '@/components/notices/CardSetupForm';
import { BatchRunPanel } from '@/components/notices/BatchRunPanel';
import { MembersList } from '@/components/notices/MembersList';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination, useTablePaginationState } from '@/components/ui/table-pagination';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';
import type { TFunction } from 'i18next';

const PERIODS: Period[] = ['spring', 'autumn', 'full_year', 'season', 'custom'];

function formatMinor(minor: number, currency: string, locale: string): string {
  const cur = currency || 'SEK';
  try {
    return new Intl.NumberFormat(locale || 'sv-SE', {
      style: 'currency',
      currency: cur,
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${cur}`;
  }
}

// Notices (Aviseringar) admin page. Pick a team + period, dry-run the fee
// engine (who owes what and why, who's unreachable, total expected), then send
// the run. Below: the try-out flow (Flow 3) and booking + seats (Flow 1), plus
// the collection dashboard with the YoY compare. Owner / administrator only —
// the backend enforces the same rule.
export function Notices() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const confirm = useConfirm();
  const qc = useQueryClient();

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const role = org?.role ?? '';
  const canManage = role === 'owner' || role === 'administrator';

  const currentYear = new Date().getFullYear();
  // Tab + drill-in are URL-driven (?tab=teams|members, ?team=<id>) so they're
  // deep-linkable (the AI navigate tool + "view history" links can target a
  // specific tab/team). Teams is the landing tab; picking a team there drills
  // into that team's detail *within* the Teams tab (?team=<id> set).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as 'teams' | 'members') || 'teams';
  const teamId = searchParams.get('team') ?? '';
  const setTab = (v: 'teams' | 'members') => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', v);
    setSearchParams(next, { replace: true });
  };
  const setTeamId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('team', id);
    else next.delete('team');
    setSearchParams(next, { replace: true });
  };
  const [period, setPeriod] = useState<Period>('autumn');
  const [year, setYear] = useState(currentYear);
  const [preview, setPreview] = useState<Preview | null>(null);
  // Client-side paging over the dry-run rows (the preview is returned whole).
  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'preview',
    defaultSize: 25,
  });

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug, { limit: 200, sort: 'name' }),
    enabled: !!token && canManage,
  });

  const membersQuery = useQuery({
    queryKey: ['notices-members', slug, teamId],
    queryFn: () => listMembers(token, slug, { teamIds: [teamId], limit: 200, sort: 'name' }),
    enabled: !!token && canManage && !!teamId,
  });

  const configQuery = useQuery({
    queryKey: ['notices-config', slug, teamId],
    queryFn: () => getNoticeConfig(token, slug, teamId),
    enabled: !!token && canManage && !!teamId,
  });
  const isBooking = !!configQuery.data?.resolved.seat_limit;

  const collectionQuery = useQuery({
    queryKey: ['notices-collection', slug, teamId, period, year],
    queryFn: () => getCollectionStatus(token, slug, teamId, period, year),
    enabled: !!token && canManage && !!teamId,
  });

  const metricsQuery = useQuery({
    queryKey: ['notices-subscription-metrics', slug, teamId],
    queryFn: () => getSubscriptionMetrics(token, slug, teamId),
    enabled: !!token && canManage && !!teamId,
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['notices-subscriptions', slug, teamId],
    queryFn: () => listSubscriptions(token, slug, { team_id: teamId }),
    enabled: !!token && canManage && !!teamId,
  });

  // The card-setup form is shown inline for one subscription at a time.
  const [cardSetup, setCardSetup] = useState<{ subId: string; init: CardSetupInit } | null>(null);

  // Saving a card for automatic monthly charges needs the member's agreement, so
  // the mandate wording is shown and confirmed before the card form opens. On the
  // admin surface the member is at the desk, and the admin confirms on their
  // behalf — the same thing a gym does at signup. A plan that already carries a
  // mandate (re-saving an expired card) skips the re-ask.
  const openCardSetup = async (sub: Subscription) => {
    if (!sub.mandate_accepted_at) {
      const club = org?.name ?? slug;
      const amount = formatMinor(sub.amount_minor, sub.currency || currency, locale);
      if (
        !(await confirm({
          title: t('notices.card.mandate.heading'),
          body: t('notices.card.mandate.recurring-v1', { club, amount }),
          confirmLabel: t('notices.card.mandate.accept'),
        }))
      ) {
        return;
      }
    }
    try {
      const init = await startCardSetup(token, slug, sub.id, true);
      setCardSetup({ subId: sub.id, init });
    } catch {
      // Stripe not configured or subscription gone — surface nothing loud here.
      setCardSetup(null);
    }
  };

  const invalidateSubs = () => {
    void qc.invalidateQueries({ queryKey: ['notices-subscriptions', slug, teamId] });
    void qc.invalidateQueries({ queryKey: ['notices-subscription-metrics', slug, teamId] });
  };

  const onCancelSubscription = async (subId: string, name: string) => {
    if (
      !(await confirm({
        title: t('notices.subs.cancelTitle'),
        body: t('notices.subs.cancelBody', { name }),
        confirmLabel: t('notices.subs.cancelCta'),
        tone: 'destructive',
      }))
    ) {
      return;
    }
    await cancelSubscription(token, slug, subId);
    invalidateSubs();
  };

  const previewMutation = useMutation({
    mutationFn: () => previewNoticeRun(token, slug, { team_id: teamId, period, year }),
    onSuccess: (p) => setPreview(p),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendNoticeRun(token, slug, { team_id: teamId, period, year }),
    onSuccess: () => {
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ['notices-collection', slug, teamId, period, year] });
    },
  });

  const locale = i18n.language;
  const currency = preview?.currency || collectionQuery.data?.currency || 'SEK';
  const canRun = !!teamId;
  // Clamp against total so a stale URL page (e.g. after switching teams) never
  // slices past the end and shows an empty body. TablePagination clamps its own
  // display the same way.
  const pagedPage = Math.min(page, Math.max(1, Math.ceil((preview?.rows.length ?? 0) / size)));

  const memberOptions = useMemo(
    () =>
      (membersQuery.data?.members ?? []).map((m) => ({
        id: m.id,
        name: `${m.first_name} ${m.last_name}`,
      })),
    [membersQuery.data],
  );

  if (!canManage) {
    return <AccessDenied />;
  }

  async function onSend() {
    if (!preview) return;
    const ok = await confirm({
      title: t('notices.send.confirmTitle'),
      body: t('notices.send.confirmBody', {
        count: preview.notice_count,
        total: formatMinor(preview.total_expected_minor, currency, locale),
      }),
      confirmLabel: t('notices.send.cta'),
      tone: preview.notice_count > 50 ? 'destructive' : 'default',
    });
    if (ok) sendMutation.mutate();
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
            <Bell weight="bold" className="h-6 w-6 shrink-0" />
            {t('notices.title')}
          </h1>
          <p className="text-sm text-foreground/70">{t('notices.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/orgs/${slug}/settings/team-categories`)}
          className="gap-1.5"
        >
          <Coins size={18} />
          {t('notices.fees.manage', { defaultValue: 'Manage fees' })}
        </Button>
      </header>

      {/* Pending try-out approvals — org-wide, shown whenever any are waiting */}
      <PendingApprovals slug={slug} token={token} />

      {/* Batch send — category / org-wide fee runs (async + bell) */}
      <BatchRunPanel
        slug={slug}
        period={period}
        year={year}
        formatMoney={(m) => formatMinor(m, currency, locale)}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'teams' | 'members')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="teams" className="gap-1.5">
              <UsersThree size={16} />
              {t('notices.tabs.teams')}
            </TabsTrigger>
            <TabsTrigger value="members" className="gap-1.5">
              <UserPlus size={16} />
              {t('notices.tabs.members')}
            </TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            onClick={() => navigate(`/orgs/${slug}/settings/activity?tab=actions&preset=notices`)}
            className="gap-1.5"
          >
            <ClockCounterClockwise size={18} />
            {t('notices.members.activityLog')}
          </Button>
        </div>

        {/* Teams tab — the overview (funnel entry) drills into a single team's
            detail in place (?team=<id> set); a back link returns to overview. */}
        <TabsContent value="teams" className="mt-4 flex flex-col gap-5">
          {!teamId && (
            <TeamsOverview
              slug={slug}
              token={token}
              teams={teamsQuery.data?.teams ?? []}
              loading={teamsQuery.isLoading}
              onSelectTeam={(id) => {
                setTeamId(id);
                setPreview(null);
              }}
            />
          )}
          {teamId && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTeamId('');
                    setPreview(null);
                  }}
                  className="gap-1.5"
                >
                  <CaretLeft size={16} />
                  {t('notices.teams.backToOverview')}
                </Button>
                <h2 className="font-heading text-lg font-bold">
                  {teamsQuery.data?.teams.find((tm) => tm.id === teamId)?.name ?? ''}
                </h2>
              </div>
      {/* Run selector */}
      <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.run.team')}</span>
            <Select
              value={teamId}
              onValueChange={(v) => {
                setTeamId(v);
                setPreview(null);
              }}
            >
              <SelectTrigger className="min-h-[var(--tap)] min-w-[12rem]">
                <SelectValue placeholder={t('notices.run.pickTeam')} />
              </SelectTrigger>
              <SelectContent>
                {teamsQuery.data?.teams.map((tm) => (
                  <SelectItem key={tm.id} value={tm.id}>
                    {tm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.run.period')}</span>
            <Select
              value={period}
              onValueChange={(v) => {
                setPeriod(v as Period);
                setPreview(null);
              }}
            >
              <SelectTrigger className="min-h-[var(--tap)] min-w-[9rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {t(`notices.period.${p}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.run.year')}</span>
            <input
              type="number"
              className="min-h-[var(--tap)] w-28 rounded-pill border border-border bg-background px-4"
              value={year}
              onChange={(e) => {
                setYear(Number(e.target.value) || currentYear);
                setPreview(null);
              }}
            />
          </label>
          <Button
            variant="outline"
            disabled={!canRun || previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
          >
            {previewMutation.isPending ? t('notices.preview.loading') : t('notices.preview.cta')}
          </Button>
        </div>
        {previewMutation.isError && (
          <p className="text-sm text-destructive">{t('notices.errors.previewFailed')}</p>
        )}
      </section>

      {/* Preview */}
      {preview && (
        <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-bold">{t('notices.preview.heading')}</h2>
            <Button disabled={preview.notice_count === 0 || sendMutation.isPending} onClick={onSend}>
              {sendMutation.isPending ? t('notices.send.sending') : t('notices.send.cta')}
            </Button>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Stat label={t('notices.preview.noticeCount')} value={String(preview.notice_count)} />
            <Stat
              label={t('notices.preview.totalExpected')}
              value={formatMinor(preview.total_expected_minor, currency, locale)}
            />
            <Stat label={t('notices.preview.feeFree')} value={String(preview.fee_free_count)} />
            <Stat
              label={t('notices.preview.missingContact')}
              value={String(preview.missing_contact_count)}
            />
          </div>

          <Table className="min-w-[40rem]">
            <TableHeader>
              <TableRow>
                <TableHead>{t('notices.table.member')}</TableHead>
                <TableHead>{t('notices.table.amount')}</TableHead>
                <TableHead>{t('notices.table.why')}</TableHead>
                <TableHead>{t('notices.table.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.rows
                .slice((pagedPage - 1) * size, (pagedPage - 1) * size + size)
                .map((row) => (
                  <TableRow key={row.member_id}>
                    <TableCell className="font-medium">{row.member_name}</TableCell>
                    <TableCell className="tabular-nums">
                      {row.total_minor > 0 ? formatMinor(row.total_minor, currency, locale) : '—'}
                    </TableCell>
                    <TableCell>
                      <ReasonCell row={row} currency={currency} locale={locale} />
                    </TableCell>
                    <TableCell>
                      {row.missing_contact ? (
                        <Badge variant="destructive" className="gap-1">
                          <WarningCircle weight="bold" className="h-3.5 w-3.5" />
                          {t('notices.table.unreachable')}
                        </Badge>
                      ) : row.would_be_noticed ? (
                        <Badge>{t('notices.table.willNotice')}</Badge>
                      ) : (
                        <Badge variant="secondary">{t('notices.table.noFee')}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            total={preview.rows.length}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={setSize}
          />
          {sendMutation.isSuccess && (
            <p className="text-sm text-foreground/70">
              {t('notices.send.done', {
                created: sendMutation.data.created.length,
                skipped: sendMutation.data.skipped_existing.length,
              })}
            </p>
          )}
        </section>
      )}

      {/* Try-out (Flow 3) — needs a selected team */}
      {teamId && (
        <TryoutSection
          slug={slug}
          token={token}
          teamId={teamId}
          period={period}
          year={year}
          members={memberOptions}
        />
      )}

      {/* Booking + seats (Flow 1) — only for seat-limited groups */}
      {teamId && isBooking && (
        <BookingSection
          slug={slug}
          token={token}
          teamId={teamId}
          period={period}
          year={year}
          members={memberOptions}
        />
      )}

      {/* Collection dashboard — the §11 money story (expected/collected/lost/
          outstanding), YoY compare and forecast range. */}
      {teamId && (
        <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
          <h2 className="font-heading text-lg font-bold">{t('notices.collection.heading')}</h2>
          <CollectionDashboard
            cs={collectionQuery.data}
            loading={collectionQuery.isLoading}
            formatMoney={(minor) => formatMinor(minor, currency, locale)}
          />
        </section>
      )}

      {/* Recurring / MRR panel (§11.1) — only shown once the team has plans. */}
      {teamId && metricsQuery.data && metricsQuery.data.active_count > 0 && (
        <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
          <h2 className="font-heading text-lg font-bold">{t('notices.recurring.heading')}</h2>
          <div className="flex flex-wrap gap-4 text-sm">
            <Stat
              label={t('notices.recurring.active')}
              value={String(metricsQuery.data.active_count)}
            />
            <Stat
              label={t('notices.recurring.mrr')}
              value={formatMinor(metricsQuery.data.mrr_minor, metricsQuery.data.currency || currency, locale)}
            />
            <Stat
              label={t('notices.recurring.projected')}
              value={formatMinor(
                metricsQuery.data.projected_next_charge_minor,
                metricsQuery.data.currency || currency,
                locale,
              )}
            />
            <Stat
              label={t('notices.recurring.churn')}
              value={String(metricsQuery.data.churn_last_30_count)}
            />
          </div>
        </section>
      )}

      {/* Subscriptions list + saved-card capture */}
      {teamId && (subscriptionsQuery.data?.subscriptions.length ?? 0) > 0 && (
        <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
          <h2 className="font-heading text-lg font-bold">{t('notices.subs.heading')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 pr-3 font-medium">{t('notices.table.member')}</th>
                  <th className="py-2 pr-3 font-medium">{t('notices.subs.plan')}</th>
                  <th className="py-2 pr-3 font-medium">{t('notices.subs.amount')}</th>
                  <th className="py-2 pr-3 font-medium">{t('notices.subs.card')}</th>
                  <th className="py-2 font-medium">{t('notices.table.status')}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {subscriptionsQuery.data?.subscriptions.map((sub) => (
                  <tr key={sub.id} className="border-b border-border/50 align-top">
                    <td className="py-2 pr-3">{sub.member_name}</td>
                    <td className="py-2 pr-3">{t(`notices.subs.plan_${sub.plan_kind}`)}</td>
                    <td className="py-2 pr-3 tabular-nums">
                      {formatMinor(sub.amount_minor, sub.currency || currency, locale)}
                    </td>
                    <td className="py-2 pr-3">
                      {cardSetup?.subId === sub.id ? (
                        <div className="min-w-[18rem]">
                          <CardSetupForm
                            clientSecret={cardSetup.init.client_secret}
                            stripeAccountId={cardSetup.init.stripe_account_id}
                            onDone={() => {
                              setCardSetup(null);
                              invalidateSubs();
                            }}
                            onCancel={() => setCardSetup(null)}
                          />
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => void openCardSetup(sub)}
                          disabled={sub.status !== 'active'}
                        >
                          {t('notices.subs.saveCard')}
                        </Button>
                      )}
                    </td>
                    <td className="py-2">
                      <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                        {t(`notices.subs.status_${sub.status}`)}
                      </Badge>
                    </td>
                    <td className="py-2 text-right">
                      {(sub.status === 'active' || sub.status === 'paused') && (
                        <Button
                          variant="outline"
                          onClick={() => void onCancelSubscription(sub.id, sub.member_name)}
                        >
                          {t('notices.subs.cancel')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
            </>
          )}
        </TabsContent>

        {/* Members tab — the member-centric worklist (find a person, see their
            status, resend a notice, or edit their fees). */}
        <TabsContent value="members" className="mt-4">
          <MembersList slug={slug} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface MemberOption {
  id: string;
  name: string;
}

interface SectionProps {
  slug: string;
  token: string | null;
  teamId: string;
  period: Period;
  year: number;
  members: MemberOption[];
}

// TeamsOverview is the "Teams" tab: one scannable row per team showing whether
// a notice run has been started and the headline counts (outstanding / paid).
// Clicking a row drills into the Members tab with that team preselected. Only
// teams with notices come back from the aggregate; the rest render as
// "not started" with zero counts by merging against the full team list.
function TeamsOverview({
  slug,
  token,
  teams,
  loading,
  onSelectTeam,
}: {
  slug: string;
  token: string | null;
  teams: Team[];
  loading: boolean;
  onSelectTeam: (teamId: string) => void;
}) {
  const { t, i18n } = useTranslation('common');

  const overviewQuery = useQuery({
    queryKey: ['notices-teams-overview', slug],
    queryFn: () => listTeamNoticeOverview(token, slug),
    enabled: !!token,
  });

  const statByTeam = useMemo(() => {
    const m = new Map<string, TeamNoticeStat>();
    for (const s of overviewQuery.data?.teams ?? []) m.set(s.team_id, s);
    return m;
  }, [overviewQuery.data]);

  // Client-side paging over the (already-fetched) team list, same pattern as the
  // other list pages (Shop, Members). Clamp so a stale URL page never slices past
  // the end.
  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'teams',
    defaultSize: 25,
  });
  const pagedPage = Math.min(page, Math.max(1, Math.ceil(teams.length / size)));
  const pageTeams = teams.slice((pagedPage - 1) * size, (pagedPage - 1) * size + size);

  const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(i18n.language || 'sv-SE', { dateStyle: 'medium' }).format(
        new Date(iso),
      );
    } catch {
      return '—';
    }
  };

  if (loading || overviewQuery.isLoading) {
    return <TableSkeleton columns={6} />;
  }

  if (teams.length === 0) {
    return (
      <EmptyState icon={<UsersThree />} title={t('notices.teams.empty')} />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="font-heading text-lg font-bold">{t('notices.teams.heading')}</h2>
        <p className="text-sm text-foreground/70">{t('notices.teams.subtitle')}</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table className="min-w-[44rem]">
          <TableHeader>
            <TableRow>
              <TableHead>{t('notices.teams.col.team')}</TableHead>
              <TableHead>{t('notices.teams.col.status')}</TableHead>
              <TableHead className="text-right">{t('notices.teams.col.notices')}</TableHead>
              <TableHead className="text-right">{t('notices.teams.col.outstanding')}</TableHead>
              <TableHead className="text-right">{t('notices.teams.col.paid')}</TableHead>
              <TableHead>{t('notices.teams.col.lastActivity')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageTeams.map((tm) => {
              const stat = statByTeam.get(tm.id);
              const started = (stat?.notice_count ?? 0) > 0;
              return (
                <TableRow
                  key={tm.id}
                  onClick={() => onSelectTeam(tm.id)}
                  tabIndex={0}
                  role="button"
                  aria-label={t('notices.teams.openTeam', { name: tm.name })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectTeam(tm.id);
                    }
                  }}
                  className="cursor-pointer transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                >
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-1.5">
                      {tm.name}
                      <CaretRight size={14} className="text-foreground/30" />
                    </span>
                  </TableCell>
                  <TableCell>
                    {started ? (
                      <Badge className="gap-1">
                        <Check weight="bold" className="h-3.5 w-3.5" />
                        {t('notices.teams.started')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">{t('notices.teams.notStarted')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{stat?.notice_count ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {stat?.outstanding_count ?? 0}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{stat?.paid_count ?? 0}</TableCell>
                  <TableCell className="text-foreground/70">{fmtDate(stat?.last_activity)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={teams.length}
        page={page}
        size={size}
        onPageChange={setPage}
        onSizeChange={setSize}
      />
    </div>
  );
}

// PendingApprovals lists every try-out held for a leader decision (Flow 3b),
// oldest first, with one-click approve / decline. Org-wide, so it shows above
// the team selector.
function PendingApprovals({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const qc = useQueryClient();
  const key = ['notices-pending-approvals', slug];

  const pendingQuery = useQuery({
    queryKey: key,
    queryFn: () => listPendingApprovals(token, slug),
    enabled: !!token,
  });

  const approveM = useMutation({
    mutationFn: (noticeId: string) => approveTryout(token, slug, noticeId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const declineM = useMutation({
    mutationFn: (noticeId: string) => declineTryout(token, slug, noticeId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  const pending = pendingQuery.data?.pending ?? [];
  if (pending.length === 0) return null;

  async function onDecline(noticeId: string, name: string) {
    const ok = await confirm({
      title: t('notices.approvals.declineConfirmTitle'),
      body: t('notices.approvals.declineConfirmBody', { name }),
      confirmLabel: t('notices.approvals.decline'),
      tone: 'destructive',
    });
    if (ok) declineM.mutate(noticeId);
  }

  const busy = approveM.isPending || declineM.isPending;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
        <UserPlus weight="bold" className="h-5 w-5 shrink-0" />
        {t('notices.approvals.heading', { count: pending.length })}
      </h2>
      <ul className="flex flex-col divide-y divide-border/50">
        {pending.map((p) => (
          <li key={p.notice_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span className="text-sm font-medium">{p.member_name}</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={() => approveM.mutate(p.notice_id)}
                className="gap-1"
              >
                <Check weight="bold" className="h-4 w-4" />
                {t('notices.approvals.approve')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onDecline(p.notice_id, p.member_name)}
                className="gap-1"
              >
                <X weight="bold" className="h-4 w-4" />
                {t('notices.approvals.decline')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// TryoutSection creates a try-out notice for a member: open (live now) or held
// for leader approval, with a weeks/sessions trial clock.
function TryoutSection({ slug, token, teamId, period, year, members }: SectionProps) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const [memberId, setMemberId] = useState('');
  const [requireApproval, setRequireApproval] = useState(false);
  const [mode, setMode] = useState<'weeks' | 'sessions'>('weeks');
  const [length, setLength] = useState(3);

  const createM = useMutation({
    mutationFn: () =>
      createTryout(token, slug, {
        team_id: teamId,
        member_id: memberId,
        period,
        year,
        require_approval: requireApproval,
        mode,
        length,
      }),
    onSuccess: () => {
      setMemberId('');
      void qc.invalidateQueries({ queryKey: ['notices-pending-approvals', slug] });
    },
  });

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
      <h2 className="font-heading text-lg font-bold">{t('notices.tryout.heading')}</h2>
      <p className="text-sm text-foreground/60">
        {requireApproval ? t('notices.tryout.approvalHint') : t('notices.tryout.openHint')}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('notices.tryout.member')}</span>
          <Select value={memberId} onValueChange={setMemberId}>
            <SelectTrigger className="min-h-[var(--tap)] min-w-[12rem]">
              <SelectValue placeholder={t('notices.tryout.pickMember')} />
            </SelectTrigger>
            <SelectContent>
              {members.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('notices.tryout.mode')}</span>
          <Select value={mode} onValueChange={(v) => setMode(v as 'weeks' | 'sessions')}>
            <SelectTrigger className="min-h-[var(--tap)] min-w-[9rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weeks">{t('notices.tryout.weeks')}</SelectItem>
              <SelectItem value="sessions">{t('notices.tryout.sessions')}</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('notices.tryout.length')}</span>
          <input
            type="number"
            min={1}
            className="min-h-[var(--tap)] w-24 rounded-pill border border-border bg-background px-4"
            value={length}
            onChange={(e) => setLength(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
        <label className="flex min-h-[var(--tap)] cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={requireApproval}
            onChange={(e) => setRequireApproval(e.target.checked)}
          />
          <span className="font-medium">{t('notices.tryout.requireApproval')}</span>
        </label>
        <Button disabled={!memberId || createM.isPending} onClick={() => createM.mutate()}>
          {createM.isPending ? t('notices.tryout.creating') : t('notices.tryout.cta')}
        </Button>
      </div>
      {createM.isError && <p className="text-sm text-destructive">{t('notices.errors.tryoutFailed')}</p>}
      {createM.isSuccess && <p className="text-sm text-foreground/70">{t('notices.tryout.created')}</p>}
    </section>
  );
}

// BookingSection shows seat occupancy for a seat-limited group, books a member
// (or waitlists when full), and lists seat holders + the waiting list with a
// release action that frees capacity.
function BookingSection({ slug, token, teamId, period, year, members }: SectionProps) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const [memberId, setMemberId] = useState('');

  const summaryKey = ['notices-seat-summary', slug, teamId, period, year];
  const listKey = ['notices-seats', slug, teamId, period, year];

  const summaryQuery = useQuery({
    queryKey: summaryKey,
    queryFn: () => getSeatSummary(token, slug, teamId, period, year),
    enabled: !!token,
  });
  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: () => listSeats(token, slug, teamId, period, year),
    enabled: !!token,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: summaryKey });
    void qc.invalidateQueries({ queryKey: listKey });
  };

  const bookM = useMutation({
    mutationFn: () => bookSeat(token, slug, { team_id: teamId, member_id: memberId, period, year }),
    onSuccess: () => {
      setMemberId('');
      invalidate();
    },
  });
  const releaseM = useMutation({
    mutationFn: (seatId: string) => releaseSeat(token, slug, seatId),
    onSuccess: invalidate,
  });

  const sum = summaryQuery.data;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
        <Ticket weight="bold" className="h-5 w-5 shrink-0" />
        {t('notices.booking.heading')}
      </h2>

      {sum && (
        <div className="flex flex-wrap gap-4 text-sm">
          <Stat label={t('notices.booking.seatLimit')} value={String(sum.seat_limit)} />
          <Stat label={t('notices.booking.available')} value={String(sum.available)} />
          <Stat label={t('notices.booking.held')} value={String(sum.held)} />
          <Stat label={t('notices.booking.confirmed')} value={String(sum.confirmed)} />
          <Stat label={t('notices.booking.waiting')} value={String(sum.waiting)} />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('notices.booking.member')}</span>
          <Select value={memberId} onValueChange={setMemberId}>
            <SelectTrigger className="min-h-[var(--tap)] min-w-[12rem]">
              <SelectValue placeholder={t('notices.booking.pickMember')} />
            </SelectTrigger>
            <SelectContent>
              {members.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <Button disabled={!memberId || bookM.isPending} onClick={() => bookM.mutate()}>
          {bookM.isPending ? t('notices.booking.booking') : t('notices.booking.book')}
        </Button>
        {bookM.isSuccess && (
          <span className="text-sm text-foreground/70">
            {bookM.data.waitlisted
              ? t('notices.booking.waitlisted', { position: bookM.data.position })
              : t('notices.booking.booked')}
          </span>
        )}
        {bookM.isError && <span className="text-sm text-destructive">{t('notices.errors.bookFailed')}</span>}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-xs uppercase tracking-wider text-foreground/50">
            {t('notices.booking.seatsHeading')}
          </h3>
          {listQuery.data && listQuery.data.bookings.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border/50">
              {listQuery.data.bookings.map((b) => (
                <li key={b.seat_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span>
                    {b.member_name}{' '}
                    <Badge variant="secondary">{t(`notices.booking.status.${b.status}`)}</Badge>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={releaseM.isPending}
                    onClick={() => releaseM.mutate(b.seat_id)}
                  >
                    {t('notices.booking.release')}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-foreground/60">{t('notices.booking.noSeats')}</p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-xs uppercase tracking-wider text-foreground/50">
            {t('notices.booking.waitlistHeading')}
          </h3>
          {listQuery.data && listQuery.data.watchers.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border/50">
              {listQuery.data.watchers.map((wt) => {
                const pos = `${wt.position}.`;
                return (
                  <li key={wt.member_id} className="flex items-center gap-2 py-2 text-sm">
                    <span className="tabular-nums text-foreground/50">{pos}</span>
                    <span>{wt.member_name}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-foreground/60">{t('notices.booking.noWaiters')}</p>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-[7rem] flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wider text-foreground/50">{label}</span>
      <span className="font-heading text-lg font-bold tabular-nums">{value}</span>
    </div>
  );
}

// ReasonCategory collapses the per-fee reason codes into one scannable badge
// per member. Owing anything → 'charged'; otherwise the dominant skip reason
// decides the tone (a deferred try-out outranks a waiver outranks already-paid
// outranks "no rule configured").
type ReasonCategory = 'charged' | 'deferred' | 'waived' | 'paid' | 'none';

function reasonCategory(row: PreviewRow): ReasonCategory {
  if (row.would_be_noticed) return 'charged';
  const codes = row.reasons.map((r) => r.reason);
  if (codes.includes('tryout_deferred')) return 'deferred';
  if (codes.some((c) => c.startsWith('exempt_') || c === 'free_by_rule')) return 'waived';
  if (codes.includes('membership_paid')) return 'paid';
  return 'none';
}

// Scannable tints, following the ModeBadge convention (Signups.tsx): a light
// tint + ring in light mode, a translucent fill in dark mode.
const REASON_TONE: Record<ReasonCategory, string> = {
  charged:
    'bg-lime-100 text-lime-950 ring-lime-300/70 dark:bg-lime-500/15 dark:text-lime-100 dark:ring-lime-500/40',
  deferred:
    'bg-amber-100 text-amber-900 ring-amber-300/70 dark:bg-amber-500/15 dark:text-amber-100 dark:ring-amber-500/40',
  waived:
    'bg-sky-100 text-sky-900 ring-sky-300/70 dark:bg-sky-500/15 dark:text-sky-100 dark:ring-sky-500/40',
  paid:
    'bg-emerald-100 text-emerald-900 ring-emerald-300/70 dark:bg-emerald-500/15 dark:text-emerald-100 dark:ring-emerald-500/40',
  none: 'bg-muted text-foreground/70 ring-border',
};

// feeKindLabel renders a fee line/skip's subject: the localised kind, with a
// period suffix for training/addon, or the addon's own passthrough label.
function feeKindLabel(t: TFunction, r: FeeReason): string {
  if (r.kind === 'addon' && r.label) return r.label;
  const kind = t(`notices.feeKind.${r.kind}`);
  if (r.period && r.kind !== 'membership') {
    return `${kind} (${t(`notices.period.${r.period}`, { defaultValue: r.period })})`;
  }
  return kind;
}

// ReasonCell is the "Why" column: a colour-coded category pill plus an info
// button that opens the full per-fee breakdown, all localised from the reason
// codes the fee engine emits.
function ReasonCell({
  row,
  currency,
  locale,
}: {
  row: PreviewRow;
  currency: string;
  locale: string;
}) {
  const { t } = useTranslation('common');
  const category = reasonCategory(row);
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={cn(
          'inline-flex items-center rounded-pill px-2.5 py-0.5 text-xs font-semibold ring-1',
          REASON_TONE[category],
        )}
      >
        {t(`notices.reason.category.${category}`)}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('notices.reason.detailsLabel', { name: row.member_name })}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-pill text-foreground/45 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72">
          <div className="flex flex-col gap-2.5">
            <p className="font-heading text-sm font-semibold">{row.member_name}</p>
            {row.reasons.length === 0 ? (
              <p className="text-sm text-foreground/60">{t('notices.reason.category.none')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5 text-sm">
                {row.reasons.map((r, i) => (
                  <li key={i} className="flex items-start justify-between gap-3">
                    <span className="text-foreground/70">{feeKindLabel(t, r)}</span>
                    {r.owed ? (
                      <span className="shrink-0 font-semibold tabular-nums">
                        {formatMinor(r.amount_minor ?? 0, currency, locale)}
                      </span>
                    ) : (
                      <span className="shrink-0 text-right text-foreground/55">
                        {t(`notices.reason.code.${r.reason}`, { defaultValue: r.reason })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
